import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal } from '../common';
import { ExternalLink, UserPlus } from 'lucide-react';
import StudentAssignModal from './StudentAssignModal';

/**
 * ExamsByPresetModal - 프리셋별 시험 정보 모달
 *
 * @param {boolean} isOpen - 모달 열림 상태
 * @param {Function} onClose - 모달 닫기 함수
 * @param {string} presetName - 프리셋명
 * @param {number} presetId - 프리셋 ID
 * @param {Array} exams - 시험 정보 배열
 * @param {boolean} isLoading - 로딩 상태
 */
const ExamsByPresetModal = ({
  isOpen,
  onClose,
  presetName,
  presetId,
  exams = [],
  isLoading = false,
  onRefreshExams
}) => {
  const navigate = useNavigate();
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);

  // 날짜 포맷팅 (MM-DD)
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return `${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  };

  // 정답률 계산
  const calculateAccuracy = (correct, total) => {
    if (!total || total === 0) return 0;
    return Math.round((correct / total) * 100);
  };

  // 결과 페이지로 이동
  const handleViewResult = (studentId, stampId, examId) => {
    onClose();
    navigate(`/student/${studentId}/stamp/${stampId}/result/${examId}`);
  };

  return (
    <>
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`시험 정보 - ${presetName || ''}`}
      size="content"
      footer={
        <div className="flex w-full justify-between">
          <button
            onClick={() => setIsAssignModalOpen(true)}
            disabled={!presetId}
            className="inline-flex items-center gap-1 rounded-xl bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-hover disabled:bg-gray-300"
          >
            <UserPlus className="size-4" />
            학생배정
          </button>
          <button
            onClick={onClose}
            className="rounded-xl bg-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-300"
          >
            닫기
          </button>
        </div>
      }
    >
      {isLoading ? (
        <div className="flex h-32 items-center justify-center">
          <div className="size-8 animate-spin rounded-full border-4 border-gray-200 border-t-primary"></div>
        </div>
      ) : exams.length === 0 ? (
        <div className="flex h-32 items-center justify-center text-gray-500">
          시험 정보가 없습니다.
        </div>
      ) : (
        <>
        {(() => {
          const closedExams = exams.filter(e => e.is_closed);
          const totalCorrect = closedExams.reduce((sum, e) => sum + (e.correct_count ?? 0), 0);
          const totalWrong = closedExams.reduce((sum, e) => sum + (e.wrong_count ?? 0), 0);
          const totalProblems = closedExams.reduce((sum, e) => sum + (e.total_problems ?? 0), 0);
          const avgAccuracy = totalProblems > 0 ? Math.round((totalCorrect / totalProblems) * 100) : 0;
          return (
            <div className="mb-3 flex items-center whitespace-nowrap rounded-xl bg-gray-50 px-4 py-3 text-xs">
              <span className="shrink-0 font-semibold text-gray-800">시험 전체 결과</span>
              <div className="ml-auto flex shrink-0 items-center gap-4">
                <div className="flex items-center gap-1">
                  <span className="text-gray-500">정답</span>
                  <span className="font-semibold text-green-600">{totalCorrect}</span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-gray-500">오답</span>
                  <span className="font-semibold text-red-600">{totalWrong}</span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-gray-500">총문제</span>
                  <span className="font-semibold text-gray-900">{totalProblems}</span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-gray-500">전체 정답률</span>
                  <span className={`text-sm font-bold ${
                    avgAccuracy >= 80 ? 'text-green-600' :
                    avgAccuracy >= 60 ? 'text-yellow-600' :
                    'text-red-600'
                  }`}>
                    {avgAccuracy}%
                  </span>
                </div>
              </div>
            </div>
          );
        })()}
        <div className="overflow-x-auto">
          <table className="w-auto text-sm">
            <thead className="bg-gray-100">
              <tr>
                <th className="whitespace-nowrap px-3 py-2 text-left font-medium text-gray-700">TID</th>
                <th className="whitespace-nowrap px-3 py-2 text-left font-medium text-gray-700">SID</th>
                <th className="whitespace-nowrap px-3 py-2 text-left font-medium text-gray-700">학생명</th>
                <th className="whitespace-nowrap px-3 py-2 text-left font-medium text-gray-700">학교/학년</th>
                <th className="whitespace-nowrap px-3 py-2 text-left font-medium text-gray-700">담당/강의</th>
                <th className="whitespace-nowrap px-3 py-2 text-center font-medium text-gray-700">정답/오답/총문제</th>
                <th className="whitespace-nowrap px-3 py-2 text-center font-medium text-gray-700">정답률</th>
                <th className="whitespace-nowrap px-3 py-2 text-center font-medium text-gray-700">제출일</th>
                <th className="whitespace-nowrap px-3 py-2 text-center font-medium text-gray-700">결과</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {exams.map((exam) => {
                const isClosed = exam.is_closed;
                const accuracy = calculateAccuracy(exam.correct_count, exam.total_problems);
                return (
                  <tr key={exam.exam_id} className={`hover:bg-gray-50 ${!isClosed ? 'bg-gray-50/50' : ''}`}>
                    <td className="whitespace-nowrap px-3 py-2 text-gray-600">
                      {exam.exam_id || '-'}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-gray-600">
                      {exam.student_id || '-'}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 font-medium text-gray-900">
                      {exam.student_name || '-'}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-gray-600">
                      <div className="flex items-center gap-2">
                        <span>{exam.school_name || '-'}</span>
                        {exam.school_grade && (
                          <span className="text-xs text-gray-500">({exam.school_grade})</span>
                        )}
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-gray-600">
                      <div className="flex items-center gap-2">
                        <span>{exam.in_charge_teacher || '-'}</span>
                        {exam.lecture && (
                          <span className="text-xs text-gray-500">({exam.lecture})</span>
                        )}
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-center text-gray-900">
                      {isClosed ? (
                        <>
                          <span className="text-green-600">{exam.correct_count ?? 0}</span>
                          {' / '}
                          <span className="text-red-600">{exam.wrong_count ?? 0}</span>
                          {' / '}
                          <span>{exam.total_problems ?? 0}</span>
                        </>
                      ) : (
                        <span className="text-xs text-gray-400">미입력</span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-center">
                      {isClosed ? (
                        <span className={`font-medium ${
                          accuracy >= 80 ? 'text-green-600' :
                          accuracy >= 60 ? 'text-yellow-600' :
                          'text-red-600'
                        }`}>
                          {accuracy}%
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">-</span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-center text-gray-600">
                      {formatDate(exam.created_at)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-center">
                      {exam.student_id && exam.stamp_id ? (
                        <button
                          onClick={() => handleViewResult(exam.student_id, exam.stamp_id, exam.exam_id)}
                          className="inline-flex items-center gap-1 rounded-lg bg-primary px-2 py-1 text-xs font-medium text-white hover:bg-primary-dark"
                          title="결과 보기"
                        >
                          <ExternalLink className="size-3" />
                          결과
                        </button>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        </>
      )}
    </Modal>

    <StudentAssignModal
      isOpen={isAssignModalOpen}
      onClose={() => setIsAssignModalOpen(false)}
      presetId={presetId}
      presetName={presetName}
      onAssignSuccess={onRefreshExams}
    />
    </>
  );
};

export default ExamsByPresetModal;
